import './card.css'

const ELEMENT_NAME = 'wp-card'

/**
 * A custom web component representing a Card with observable state.
 *
 * Usage:
 * ```html
 * <wp-card title="My Card" content="This is a card."></wp-card>
 * ```
 *
 * ```html
 * <wp-card><header><h2>My Card</h2></header><section><p>This is a card.</p></section><footer></footer></wp-card>
 * ```
 *
 * You can also create it programmatically:
 * ```js
 * const card = new Card({ title: 'My Card', content: 'This is a card.', state: sharedState });
 * document.body.appendChild(card);
 * ```
 *
 * The `state` option allows sharing observable state between multiple Card instances.
 *
 * Example:
 * ```js
 * const card1 = new Card({ title: 'Card 1', content: 'This is the first card.'});
 * const card2 = new Card({ title: 'Card 2', content: 'This is the second card.' });
 * ```
 * When either card increments the shared state's counter, both cards will update automatically.
 *
 * Update: attributes, properties, methods?
 *
 * Events:
 * ppRendered() - after each render
 *
 * @param {Object} options - Configuration options for the Card.
 * @param {string} options.title - The title of the card. (Default: none)
 * @param {string} options.content - The content of the card. (Default: "Card content goes here.")
 *
 */
export default class Card extends HTMLElement {
  static observedAttributes = ['title', 'content', 'footer']

  static defaultTag = ELEMENT_NAME

  constructor (options) {
    super()

    // console.log('Card constructor', options)

    this._rendered = 0

    // Initialize component state
    this.$data = {
      title: options?.title || null,
      subtitle: options?.subtitle || null,
      content: options?.content || null,
      footer: options?.footer || null
    }

    this.$componentInfo = {
      // Component specific state
      elementId: `${ELEMENT_NAME}_` + Math.random().toString(36).substring(2, 9)
    }

    // console.log('Card constructor done', this.$componentInfo?.elementId, this.$data.title, this, this.$data)
  }

  connectedCallback () {
    // connectedCallback is executed after reading the attributes
    // console.log('Custom element added to page.', this.$componentInfo?.elementId)
    if (this._rendered > 0) return
    this.render()

    this._initDone = true
    // console.log('init done', this.$componentInfo?.elementId)
  }

  disconnectedCallback () {
    // Remove listener when Card is removed
    console.log('Custom element removed from page.')
  }

  attributeChangedCallback (name, oldValue, newValue) {
    // Only render once at init time; not for each attribute set

    console.log(`Attribute ${name} has changed from [${oldValue}] to [${newValue}]`)
    this.$data[name] = newValue

    if (oldValue === null && !this._initDone) return
    console.log('execute change of attributes')

    this.dispatchEvent(new CustomEvent('ppDataChanged', {
      detail: {
        attributeName: name,
        oldValue,
        newValue
      },
      bubbles: true,
      composed: true
    }))

    this.render()
  }

  async render () {
    // console.log('render', this.$componentInfo.elementId, this.$data)
    this.setAttribute('data-id', this.$componentInfo.elementId)
    // this.classList.add('card')

    // Wait so that all attributes will be read
    // await new Promise((resolve) => setTimeout(resolve, 1))

    // Select the real child of the component, not sub-elements
    this.$dom = {
      header: this.querySelector(':scope > header'),
      section: this.querySelector(':scope > section'),
      footer: this.querySelector(':scope > footer')
    }

    if (!this.$dom.header && this.$data.title) {
      this.$dom.header = document.createElement('header')
      this.$dom.header.innerHTML = '<h2 data-slot="title"></h2>'
      this.prepend(this.$dom.header)
    }
    if (this.$data.title) {
      if (this.$dom.header.querySelector('[data-slot="title"]')) {
        this.$dom.header.querySelector('[data-slot="title"]').textContent = this.$data.title
      }
    }

    if (!this.$dom.section && this.$data.content) {
      this.$dom.section = document.createElement('section')
      this.$dom.section.innerHTML = '<p data-slot="content"></p>'
      this.append(this.$dom.section)
    }
    this.$dom.sectionContent = this.$dom.section.querySelector('[data-slot="content"]')
    if (this.$data.content && this.$dom.sectionContent) {
      console.log('set content', this.$data)
      this.$dom.sectionContent.textContent = this.$data.content
    }

    if (!this.$dom.footer && this.$data.footer) {
      this.$dom.footer = document.createElement('footer')
      // this.$dom.footer.innerHTML = '<p><span data-slot="counter"></span><span data-slot="stateCounter"></span></p>'
      this.append(this.$dom.footer)
    }
    if (this.$data.footer) {
      this.$dom.footer = this.$data.footer
    }

    this._rendered++

    this.dispatchEvent(new CustomEvent('ppRendered', {
      detail: { rendered: this._rendered, cardInstance: this },
      bubbles: true,
      composed: true
    }))
  }
}

if (!customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, Card)
}
